import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Check, Clock3, X, Play, Flag, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { MOCK_GUIDES } from "../data/guides";
import BusLoading from "../components/BusLoading";
import DriverDashboardPage from "./Drivers/DriverDashboardPage";
import ProfileAvatar from "../components/ProfileAvatar";
import ClientDashboardPage from "./ClientDashboardPage";

const statusIcons = { new: Clock3, accepted: Check, in_progress: Play, completed: Flag, declined: X, cancelled: X };

export default function DashboardPage() {
  const { user } = useAuth();
  if (user?.roles?.includes("client")) return <ClientDashboardPage />;
  if (user?.role === "driver") return <DriverDashboardPage />;
  if (user?.role !== "guide") return <ClientDashboardPage />;
  return <BookingDashboardPage user={user} />;
}

export function GuideWorkDashboard() {
  const { user } = useAuth();
  if (!user || user.role !== "guide" || !user.is_approved) return null;
  return <BookingDashboardPage user={user} />;
}

function BookingDashboardPage({ user }) {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const isGuide = user.role === "guide";

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("bookings").select("*").order("created_at", { ascending: false });
    const rows = data || [];
    let clientProfiles = {};
    const clientIds = [...new Set(rows.map((booking) => booking.client_id).filter(Boolean))];
    if (isGuide && clientIds.length) {
      const { data: publicProfiles } = await supabase.rpc("get_public_profiles", { profile_ids: clientIds });
      clientProfiles = Object.fromEntries((publicProfiles || []).map((profile) => [profile.id, profile]));
    }
    setBookings(rows.map((booking) => ({ ...booking, client_profile: clientProfiles[booking.client_id] })));
    setLoading(false);
  }, [isGuide]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await supabase.from("bookings").update({ status }).eq("id", id);
    await load();
  };

  if (isGuide && !user.is_approved) {
    return <main className="min-h-[75vh] bg-[#F7F6F3] dark:bg-slate-950 px-4 py-12"><div className="max-w-2xl mx-auto bg-white dark:bg-slate-900 rounded-3xl p-8 border border-amber-300/40 text-center"><Clock3 className="w-10 h-10 text-amber-500 mx-auto mb-4" /><h1 className="text-2xl font-bold mb-2">{t("dashboard.pending_title")}</h1><p className="text-sm text-slate-500 dark:text-slate-400">{t("dashboard.pending_text")}</p></div></main>;
  }

  return (
    <main className="min-h-screen bg-[#F7F6F3] dark:bg-slate-950 px-4 py-10 sm:px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400">{isGuide ? t("dashboard.guide_badge") : t("dashboard.client_badge")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold mt-2 mb-8">{t("dashboard.title", { name: user.name })}</h1>
        {loading ? <BusLoading compact title={t("dashboard.loading")} /> : bookings.length === 0 ? <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center text-slate-500">{t("dashboard.empty")}</div> : (
          <div className="grid gap-4">
            {bookings.map((booking) => {
              const guide = MOCK_GUIDES.find((item) => item.id === booking.guide_slug);
              const Icon = statusIcons[booking.status] || Clock3;
              return <article key={booking.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 font-bold mb-2"><Icon className="w-4 h-4" />{t(`booking.status.${booking.status}`)}</div>
                    <h2 className="font-bold text-lg">{isGuide && booking.client_id ? <Link to={`/profile/${booking.client_id}`} className="inline-flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-400"><ProfileAvatar avatarUrl={booking.client_profile?.avatar_url} name={booking.client_profile?.full_name || booking.client_name} className="h-8 w-8" textClassName="text-[10px]" /><span>{booking.client_profile?.full_name || booking.client_name}</span></Link> : isGuide ? booking.client_name : t(`guides_data.${guide?.id}.name`, guide?.name || booking.guide_slug)}</h2>
                    {isGuide && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><UserRound className="w-3.5 h-3.5" />{booking.client_email}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-amber-500" />{new Date(`${booking.travel_date}T00:00:00`).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5 text-amber-500" />{booking.travel_time?.slice(0, 5) || t("booking.time_pending")}</span>
                    </div>
                    {booking.message && <p className="text-sm text-slate-600 dark:text-slate-300 mt-3 whitespace-pre-wrap">{booking.message}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
                  {isGuide && booking.status === "new" && <><button onClick={() => updateStatus(booking.id, "accepted")} className="px-4 py-2 rounded-full bg-amber-400 text-slate-950 text-xs font-bold">{t("dashboard.accept")}</button><button onClick={() => updateStatus(booking.id, "declined")} className="px-4 py-2 rounded-full border border-red-300 text-red-500 text-xs font-bold">{t("dashboard.decline")}</button></>}
                  {isGuide && booking.status === "accepted" && <button onClick={() => updateStatus(booking.id, "in_progress")} className="px-4 py-2 rounded-full bg-amber-400 text-slate-950 text-xs font-bold">{t("dashboard.start")}</button>}
                  {isGuide && booking.status === "in_progress" && <button onClick={() => updateStatus(booking.id, "completed")} className="px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-bold">{t("dashboard.complete")}</button>}
                  {!isGuide && ["new", "accepted"].includes(booking.status) && <button onClick={() => updateStatus(booking.id, "cancelled")} className="px-4 py-2 rounded-full border border-red-300 text-red-500 text-xs font-bold">{t("dashboard.cancel")}</button>}
                </div>
              </article>;
            })}
          </div>
        )}
      </div>
    </main>
  );
}
